import { useState } from 'react'
import { PawPrint, Calendar, FileText } from 'lucide-react'
import { Card, ErrorBox, SectionTitle } from '../../components/ui/Card'
import { useAuth } from '../../context/AuthContext'
import { useFetch } from '../../hooks/useFetch'
import {
  getPortalPatients,
  getPortalAppointments,
  getPortalInvoices,
} from '../../api/portal'
import type { PortalPatient, PortalAppointment, PortalInvoiceSummary } from '../../api/portal'
import { SPECIES_LABELS } from '../../types/patients'
import { formatCurrency } from '@/lib/currency'
import { STATUS_LABELS as APPT_STATUS_LABELS, TYPE_LABELS } from '../../types/appointments'
import { STATUS_LABELS as INV_STATUS_LABELS, STATUS_COLORS as INV_STATUS_COLORS } from '../../types/invoices'
import type { AppointmentStatus, AppointmentType } from '../../types/appointments'
import type { InvoiceStatus } from '../../types/invoices'

type Tab = 'pets' | 'appointments' | 'invoices'

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function InvoiceStatusBadge({ status }: { status: string }) {
  const s = status as InvoiceStatus
  const colors = INV_STATUS_COLORS[s] ?? { color: 'var(--text-3)', bg: 'var(--surface-alt)' }
  return (
    <span
      className="inline-flex items-center px-2 py-[0.15rem] rounded-full text-[0.78rem] font-semibold"
      style={{ color: colors.color, background: colors.bg }}
    >
      {INV_STATUS_LABELS[s] ?? status}
    </span>
  )
}

function PetCard({ pet }: { pet: PortalPatient }) {
  const speciesLabel = SPECIES_LABELS[pet.species as keyof typeof SPECIES_LABELS] ?? pet.species

  return (
    <Card className="flex gap-4 p-4">
      {pet.photo_url ? (
        <img
          src={pet.photo_url}
          alt={pet.name}
          className="w-16 h-16 rounded-full object-cover flex-shrink-0"
        />
      ) : (
        <div className="w-16 h-16 rounded-full bg-surface-alt flex items-center justify-center flex-shrink-0">
          <PawPrint size={28} strokeWidth={1.5} className="text-text-3" />
        </div>
      )}
      <div className="min-w-0">
        <div className="font-semibold text-text-heading text-[1rem] truncate">{pet.name}</div>
        <div className="text-[0.85rem] text-text-2 mt-[0.15rem]">
          {speciesLabel}{pet.breed ? ` · ${pet.breed}` : ''}
        </div>
        {pet.weight_kg != null && (
          <div className="text-[0.82rem] text-text-3 mt-[0.1rem]">{pet.weight_kg} kg</div>
        )}
        {pet.color && (
          <div className="text-[0.82rem] text-text-3">{pet.color}</div>
        )}
        {pet.date_of_birth && (
          <div className="text-[0.82rem] text-text-3">DOB {pet.date_of_birth}</div>
        )}
      </div>
    </Card>
  )
}

function AppointmentRow({ appt }: { appt: PortalAppointment }) {
  return (
    <div className="flex items-start gap-4 py-3 border-b border-border last:border-0">
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-text text-[0.9rem]">
          {appt.patient_name ?? `Patient #${appt.patient_id}`}
        </div>
        <div className="text-[0.82rem] text-text-2 mt-[0.1rem]">
          {TYPE_LABELS[appt.appointment_type as AppointmentType] ?? appt.appointment_type}
          {appt.reason ? ` — ${appt.reason}` : ''}
        </div>
        {appt.practice_name && (
          <div className="text-[0.8rem] text-text-3 mt-[0.1rem]">{appt.practice_name}</div>
        )}
      </div>
      <div className="text-right flex-shrink-0">
        <div className="text-[0.875rem] font-semibold text-text">{formatDateTime(appt.scheduled_at)}</div>
        <div className="text-[0.78rem] text-text-3 mt-[0.1rem]">{appt.duration_minutes} min</div>
        <div className="text-[0.78rem] text-text-2 mt-[0.1rem]">
          {APPT_STATUS_LABELS[appt.status as AppointmentStatus] ?? appt.status}
        </div>
      </div>
    </div>
  )
}

function InvoiceRow({ inv }: { inv: PortalInvoiceSummary }) {
  return (
    <div className="flex items-center gap-4 py-3 border-b border-border last:border-0">
      <div className="flex-1 min-w-0">
        <div className="font-mono text-[0.875rem] font-semibold text-text">{inv.invoice_number}</div>
        {inv.patient_name && (
          <div className="text-[0.8rem] text-text-3 mt-[0.1rem]">{inv.patient_name}</div>
        )}
        <div className="text-[0.8rem] text-text-3">{formatDate(inv.issued_date)}</div>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="font-semibold text-text text-[0.9rem]">{formatCurrency(inv.total_amount)}</div>
        <div className="mt-1"><InvoiceStatusBadge status={inv.status} /></div>
        {inv.due_date && inv.status !== 'paid' && inv.status !== 'cancelled' && (
          <div className={`text-[0.75rem] mt-[0.2rem] ${new Date(inv.due_date) < new Date() ? 'text-danger-text' : 'text-text-3'}`}>
            Due {formatDate(inv.due_date)}
          </div>
        )}
      </div>
    </div>
  )
}

export default function ClientDashboard() {
  const { user } = useAuth()
  const [tab, setTab] = useState<Tab>('pets')

  const pets = useFetch(() => getPortalPatients(), [])
  const appointments = useFetch(() => getPortalAppointments(), [])
  const invoices = useFetch(() => getPortalInvoices(), [])

  const tabs: { key: Tab; label: string; icon: React.ReactNode; count?: number }[] = [
    {
      key: 'pets',
      label: 'My Pets',
      icon: <PawPrint size={15} strokeWidth={2} />,
      count: pets.data?.length,
    },
    {
      key: 'appointments',
      label: 'Appointments',
      icon: <Calendar size={15} strokeWidth={2} />,
      count: appointments.data?.length,
    },
    {
      key: 'invoices',
      label: 'Invoices',
      icon: <FileText size={15} strokeWidth={2} />,
      count: invoices.data?.length,
    },
  ]

  return (
    <div className="max-w-[760px]">
      <div className="mb-6">
        <h1 className="m-0 mb-1 text-[1.4rem] font-bold text-text-heading">
          My Pet Portal
        </h1>
        <p className="m-0 text-[0.9rem] text-text-3">
          Welcome, {user?.full_name ?? user?.email}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-border">
        {tabs.map(({ key, label, icon, count }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={[
              'flex items-center gap-1.5 px-4 py-2 text-[0.875rem] font-medium border-b-2 -mb-px transition-colors',
              tab === key
                ? 'border-accent text-accent'
                : 'border-transparent text-text-3 hover:text-text',
            ].join(' ')}
          >
            {icon}
            {label}
            {count !== undefined && count > 0 && (
              <span className={`text-[0.75rem] px-1.5 py-0 rounded-full font-semibold ${tab === key ? 'bg-accent text-accent-contrast' : 'bg-border text-text-2'}`}>
                {count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Pets tab */}
      {tab === 'pets' && (
        <div>
          {pets.loading && <div className="text-text-2 py-4 text-sm">Loading…</div>}
          {pets.error && <ErrorBox>{pets.error}</ErrorBox>}
          {!pets.loading && !pets.error && (
            pets.data && pets.data.length > 0 ? (
              <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
                {pets.data.map(pet => <PetCard key={pet.id} pet={pet} />)}
              </div>
            ) : (
              <Card className="text-center py-8">
                <div className="mb-3"><PawPrint size={32} strokeWidth={1.5} className="mx-auto text-text-4" /></div>
                <p className="m-0 text-[0.9rem] text-text-3">No pets on record. Contact your practice to add your pet.</p>
              </Card>
            )
          )}
        </div>
      )}

      {/* Appointments tab */}
      {tab === 'appointments' && (
        <div>
          {appointments.loading && <div className="text-text-2 py-4 text-sm">Loading…</div>}
          {appointments.error && <ErrorBox>{appointments.error}</ErrorBox>}
          {!appointments.loading && !appointments.error && (
            <Card className="p-0 overflow-hidden">
              <div className="px-5 pt-5 pb-2">
                <SectionTitle>Upcoming Appointments</SectionTitle>
              </div>
              <div className="px-5 pb-4">
                {appointments.data && appointments.data.length > 0 ? (
                  appointments.data.map(appt => <AppointmentRow key={appt.id} appt={appt} />)
                ) : (
                  <div className="text-center py-6">
                    <Calendar size={32} strokeWidth={1.5} className="mx-auto text-text-4 mb-2" />
                    <p className="m-0 text-[0.9rem] text-text-3">No upcoming appointments.</p>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Invoices tab */}
      {tab === 'invoices' && (
        <div>
          {invoices.loading && <div className="text-text-2 py-4 text-sm">Loading…</div>}
          {invoices.error && <ErrorBox>{invoices.error}</ErrorBox>}
          {!invoices.loading && !invoices.error && (
            <Card className="p-0 overflow-hidden">
              <div className="px-5 pt-5 pb-2">
                <SectionTitle>Invoices</SectionTitle>
              </div>
              <div className="px-5 pb-4">
                {invoices.data && invoices.data.length > 0 ? (
                  invoices.data.map(inv => <InvoiceRow key={inv.id} inv={inv} />)
                ) : (
                  <div className="text-center py-6">
                    <FileText size={32} strokeWidth={1.5} className="mx-auto text-text-4 mb-2" />
                    <p className="m-0 text-[0.9rem] text-text-3">No invoices on record.</p>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  )
}
